import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Header from './Header';
import HintIconSVG from 'assets/icons/hint-icon.svg';
import SuccesIconSVG from 'assets/icons/success-icon.svg';
import BirthChartLeft from 'assets/icons/quiz/birth-chart-left.svg';
import BirthChartRight from 'assets/icons/quiz/birth-chart-right.svg';
import { PrimaryButton } from 'components';
import { tablet } from 'styles/breakpoints';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';

interface PersonalizeBookProps {
  label: string;
  inputLabel?: string;
  questionKey: string;
  onContinue: (value: object) => void;
  onSkip: () => void;
  hasSkipButton?: boolean;
  skipButtonText?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;
  height: calc(-72px + 100vh);
  @media ${tablet} {
    overflow: hidden;
  }
`;

const InnerContainer = styled.div`
  max-width: 28.75rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 3rem 0;
  @media ${tablet} {
    padding: 1.5rem 1rem;
  }
`;

const FormInnerContainer = styled.form`
  margin-bottom: 2.625rem;
  display: flex;
  flex-direction: column;
`;

const ContinueButton = styled(PrimaryButton)`
  max-width: 28.75rem;
  width: 100%;
  margin: 0 auto;
`;

const SkipButton = styled.button`
  display: flex;
  max-width: 28.75rem;
  width: 100%;
  height: 3.5rem;
  padding: 0.75rem 1.5rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 6.25rem;
  border: 1px solid rgba(0, 0, 0, 0.6);
  background: #f3f3f8;
  color: rgba(0, 0, 0, 0.7);
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  margin-top: 0.5rem;
  cursor: pointer;
`;

const InputField = styled.input<{ hasError?: boolean; isValid?: boolean }>`
  display: flex;
  height: 4rem;
  padding: 0.75rem 1rem;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 0.75rem;
  background: #fff;
  outline: none;
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: left;
  border: ${({ hasError }) =>
    hasError ? '1px solid #FF6D5C' : '1px solid #afafaf;'};
  ${({ isValid }) => isValid && 'border: 1px solid #00A676;'}
  ::placeholder {
    color: #4d4d4d;
    font-size: 1rem;
    font-weight: 300;
    line-height: 1.5rem;
    letter-spacing: 0em;
    text-align: left;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #f5f5f5 inset !important;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
`;

const Label = styled.label`
  color: #000;
  text-align: center;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  margin-bottom: 1.5rem;
  @media ${tablet} {
    font-size: 0.8125rem;
    line-height: normal;
    margin-bottom: 1rem;
  }
`;

const ErrorMessage = styled.span`
  color: #ff0000;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const InputSuccessContainer = styled.div`
  width: 1.125rem;
  height: 1.125rem;
  position: absolute;
  right: 0.91469rem;
  bottom: 1.4rem;
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const InputErrorContainer = styled(InputSuccessContainer)`
  bottom: 2.8rem;
`;

const ButtonContainer = styled.div`
  padding-top: 1.5rem;
  width: 100%;
  @media ${tablet} {
    padding-top: 1rem;
  }
`;

const BackgroundSvgRight = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: absolute;
  top: -90px;
  right: 0;
  max-width: 52.6875rem;
  z-index: -1;
  @media ${tablet} {
    max-height: 20.78125rem;
    max-width: 20.78125rem;
    top: 63px;
    right: -106px;
    svg {
      max-height: 20.78125rem;
    }
  }
`;

const BackgroundSvgLeft = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  max-width: 52.6875rem;
  z-index: -1;
  @media ${tablet} {
    max-height: 24.8125rem;
    max-width: 13.8125rem;
  }
`;

const PersonalizeBook: FC<PersonalizeBookProps> = ({
  label,
  inputLabel,
  questionKey,
  onContinue,
  onSkip,
  hasSkipButton,
  skipButtonText,
}) => {
  const quizAnswers = useSelector((state: AppState) => state.user.quiz_answers);
  const [inputValue, setInputValue] = useState<string>(
    quizAnswers?.[questionKey] || '',
  );
  const [isTouchedInput, setIsTouchedInput] = useState<boolean>(false);

  useEffect(() => {
    setInputValue(quizAnswers?.[questionKey] || '');
  }, [quizAnswers, questionKey]);

  const validationSchemaName = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .max(15, 'Name cannot contain more than 15 letters'),
  });
  const validationSchemaSurname = Yup.object().shape({
    surname: Yup.string().max(
      16,
      'Last name cannot contain more than 16 letters',
    ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    resolver: yupResolver(
      questionKey === 'name' ? validationSchemaName : validationSchemaSurname,
    ),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = (data: any, e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedData = {
      [questionKey]: data[questionKey].trim(),
    };

    onContinue(trimmedData[questionKey]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(
      /[^a-zA-Z\s.\u00C0-\u024F\u1E00-\u1EFF\u0180-\u024F\u1D00-\u1D7F\u0400-\u04FF-]/g,
      '',
    );

    setIsTouchedInput(true);
    setInputValue(value);
  };

  const handleSkip = () => {
    if (quizAnswers?.surname) {
      onContinue('');
      return;
    }
    onSkip();
  };

  return (
    <Container>
      <InnerContainer>
        <Header title={label} titleAlign="left" />
        <FormInnerContainer onSubmit={handleSubmit(onSubmit)}>
          {inputLabel && <Label>{inputLabel}</Label>}
          {questionKey === 'name' && (
            <InputContainer>
              <InputField
                {...register('name')}
                type="text"
                placeholder="Your name"
                hasError={isTouchedInput && !!errors.name}
                isValid={!errors.name && isTouchedInput}
                onInput={handleInputChange}
                value={inputValue}
              />
              {!errors.name && isTouchedInput && (
                <InputSuccessContainer>
                  <SuccesIconSVG />
                </InputSuccessContainer>
              )}
              {errors.name && (
                <>
                  <InputErrorContainer>
                    <HintIconSVG fill="#FF6D5C" />
                  </InputErrorContainer>
                  <ErrorMessage>
                    <HintIconSVG fill="#FF6D5C" />
                    {errors.name.message}
                  </ErrorMessage>
                </>
              )}
            </InputContainer>
          )}
          {questionKey === 'surname' && (
            <InputContainer>
              <InputField
                {...register('surname')}
                type="text"
                placeholder="Your last name"
                onInput={handleInputChange}
                hasError={!!errors.surname && isTouchedInput}
                isValid={!errors.surname && isTouchedInput}
                value={inputValue}
              />
              {!errors.surname && isTouchedInput && (
                <InputSuccessContainer>
                  <SuccesIconSVG />
                </InputSuccessContainer>
              )}
              {errors.surname && (
                <>
                  <InputErrorContainer>
                    <HintIconSVG fill="#FF6D5C" />
                  </InputErrorContainer>
                  <ErrorMessage>
                    <HintIconSVG fill="#FF6D5C" />
                    {errors.surname.message}
                  </ErrorMessage>
                </>
              )}
            </InputContainer>
          )}
          <ButtonContainer>
            <ContinueButton
              disabled={
                !isValid ||
                isSubmitting ||
                (questionKey === 'surname' && !inputValue)
              }
              type="submit"
            >
              Continue
            </ContinueButton>
            {hasSkipButton && (
              <SkipButton onClick={handleSkip}>{skipButtonText}</SkipButton>
            )}
          </ButtonContainer>
        </FormInnerContainer>
        <BackgroundSvgRight>
          <BirthChartRight />
        </BackgroundSvgRight>
        <BackgroundSvgLeft>
          <BirthChartLeft />
        </BackgroundSvgLeft>
      </InnerContainer>
    </Container>
  );
};

export default PersonalizeBook;
